import { DataType, DateTime } from '@mode-switch/express';
import { Attr, Names } from '@mode-switch/model';
import {
  ConditionValue,
  EncDiscreteFilterField,
  EncField,
  EncSelectionCondition,
  Encoding,
  FilterType,
  MarkType,
  SelectionMode,
} from '@mode-vegas/vegas';
import { ChartDate, EncodingHelpers, SchemaModel, TOTALS_TOKEN } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, CompiledFormula, Switch } from '@mode/shared/util-switch';
import { addMinutes, parse } from 'date-fns';

export namespace ChartCompleteData {
  type ValueMappingFunctions = {
    boolean: (value: unknown) => boolean;
    number: (value: unknown) => number;
    string: (value: unknown) => string;
    quarter: (value: unknown) => number;
    weekday: (value: unknown) => number;
    month: (value: unknown) => number;
    utcTimestamp: (value: unknown) => number;
  };

  const QuarterNamesByNumber = ChartDate.QuarterNamesByNumber;
  const WeekdayNamesByNumber = ChartDate.WeekdayNamesByNumber;
  const MonthNamesByNumber = ChartDate.MonthNamesByNumber;

  const DefaultValueMappings: ValueMappingFunctions = {
    boolean: (value) => (value === null ? null : value === true || value === 'true'),
    number: (value) => (value === null ? null : parseFloat(value as string)),
    string: (value) => (value === null ? null : `${value}`),
    quarter: (value) => (value === null ? null : QuarterNamesByNumber[value as string]),
    weekday: (value) => (value === null ? null : WeekdayNamesByNumber[value as string]),
    month: (value) => (value === null ? null : MonthNamesByNumber[value as string]),
    utcTimestamp: (value) => (value === null ? null : new Date(value as string).valueOf()),
  };

  const DateTruncFormats: Record<DateTime.Unit, string> = {
    [DateTime.Unit.Year]: 'y',
    [DateTime.Unit.Quarter]: 'QQQ y',
    [DateTime.Unit.Month]: 'MMM y',
    [DateTime.Unit.Week]: 'MMM d y',
    [DateTime.Unit.Day]: 'MMM d y',
    [DateTime.Unit.Hour]: 'ha MMM d y',
    [DateTime.Unit.Minute]: 'h:ma MMM d y',
    [DateTime.Unit.Second]: 'h:m:sa MMM d y',
    [DateTime.Unit.DayOfYear]: '',
    [DateTime.Unit.Weekday]: '',
  };

  const VegasValueMappings: ValueMappingFunctions = {
    ...DefaultValueMappings,
    quarter: (value) => (value === null ? null : parseInt(value as string, 10)),
    weekday: (value) => (value === null ? null : parseInt(value as string, 10)),
    month: (value) => (value === null ? null : parseInt(value as string, 10)),
    utcTimestamp: (value) => value as number,
  };

  export function makeFilters(
    model: SchemaModel,
    encoding: Encoding,
    target: ChartTypes.MarkTarget
  ): EncDiscreteFilterField[] {
    let namesOnX = false;
    let fieldOnSize = false;
    let multiSeries = false;
    const markType = getMarkType(encoding);

    if (encoding.marks.series.length > 1) {
      multiSeries = true;
    }

    if (encoding.x.length > 0 && encoding.x[0].formula === Names.Key) {
      namesOnX = true;
    }

    if (encoding.marks.series.length && encoding.marks.series[0].properties.size.values.length) {
      fieldOnSize = true;
    }

    switch (markType) {
      case MarkType.Bar:
        if (multiSeries) {
          return makeCombo(model, encoding, target);
        } else {
          if (namesOnX) {
            return makeGroupedBar(model, encoding, target);
          } else {
            return makeStackedBar(model, encoding, target);
          }
        }
      default:
      case MarkType.Line:
        return makeLine(model, encoding, target);
      case MarkType.Area:
        return makeArea(model, encoding, target);
      case MarkType.TotalArea:
        return makeTotalArea(model, encoding, target);
      case MarkType.Circle:
        if (fieldOnSize) {
          return makeBubble(model, encoding, target);
        } else {
          return makeScatter(model, encoding, target);
        }
      case MarkType.Pie:
        return makePie(model, encoding, target);
      case MarkType.Donut:
        return makeDonut(model, encoding, target);
    }
  }

  export function makeVegasFilters(
    model: SchemaModel,
    encoding: Encoding,
    target: ChartTypes.MarkTarget
  ): EncDiscreteFilterField[] {
    const filters = new Array<EncDiscreteFilterField>();

    target.columns?.forEach((column) => {
      const { value, id } = column;

      // Do not add the TOTALS_TOKEN value from grand totals cells. It's not
      // a real value from the dataset and will produce an empty view data
      // result
      if (value === TOTALS_TOKEN) {
        return;
      }

      if (id) {
        const encField = EncodingHelpers.getEncFieldById(encoding, id);
        const filter = makeFilter(model, encoding, encField, value, VegasValueMappings);

        if (filter) {
          filters.push(filter);
        }
      }
    });

    return filters;
  }

  function makeLine(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncDiscreteFilterField[] {
    const filters = new Array<EncDiscreteFilterField>();

    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const xEncField = encoding.x[0];
    const colorEncField = props.color.values[0];

    if (xEncField) {
      const { value } = target.x;
      const filter = makeFilter(model, encoding, xEncField, value);

      if (filter) {
        filters.push(filter);
      }
    }

    if (colorEncField) {
      const { value } = target.series.color;
      const filter = makeSeriesFilter(model, encoding, colorEncField, value);

      if (filter) {
        filters.push(filter);
      }
    }

    return filters;
  }

  function makeStackedBar(
    model: SchemaModel,
    encoding: Encoding,
    target: ChartTypes.MarkTarget
  ): EncDiscreteFilterField[] {
    return makeLine(model, encoding, target);
  }

  function makeArea(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncDiscreteFilterField[] {
    return makeLine(model, encoding, target);
  }

  function makeTotalArea(
    model: SchemaModel,
    encoding: Encoding,
    target: ChartTypes.MarkTarget
  ): EncDiscreteFilterField[] {
    return makeLine(model, encoding, target);
  }

  function makeBubble(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncDiscreteFilterField[] {
    return makeLine(model, encoding, target);
  }

  function makeScatter(
    model: SchemaModel,
    encoding: Encoding,
    target: ChartTypes.MarkTarget
  ): EncDiscreteFilterField[] {
    return makeLine(model, encoding, target);
  }

  function makeCombo(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncDiscreteFilterField[] {
    const filters = new Array<EncDiscreteFilterField>();

    const xEncField = encoding.x[0];
    if (xEncField) {
      const { value } = target.x;
      const filter = makeFilter(model, encoding, xEncField, value);

      if (filter) {
        filters.push(filter);
      }
    }

    return filters;
  }

  function makeGroupedBar(
    model: SchemaModel,
    encoding: Encoding,
    target: ChartTypes.MarkTarget
  ): EncDiscreteFilterField[] {
    const filters = new Array<EncDiscreteFilterField>();

    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const columnEncField = encoding.column[0];
    const colorEncField = props.color.values[0];

    if (columnEncField) {
      const { value } = target.columns[0];
      const filter = makeFilter(model, encoding, columnEncField, value);

      if (filter) {
        filters.push(filter);
      }
    }

    if (colorEncField) {
      const { value } = target.series.color;
      const filter = makeSeriesFilter(model, encoding, colorEncField, value);

      if (filter) {
        filters.push(filter);
      }
    }

    return filters;
  }

  function makePie(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncDiscreteFilterField[] {
    const filters = new Array<EncDiscreteFilterField>();

    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const colorEncField = props.color.values[0];

    if (colorEncField) {
      const { value } = target.series.color;
      const filter = makeSeriesFilter(model, encoding, colorEncField, value);

      if (filter) {
        filters.push(filter);
      }
    }

    return filters;
  }

  function makeDonut(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncDiscreteFilterField[] {
    return makePie(model, encoding, target);
  }

  function getMarkType(encoding: Encoding): MarkType {
    if (encoding.marks.series.length > 0) {
      return encoding.marks.series[0].properties.type.value;
    } else {
      return encoding.marks.properties.type.value;
    }
  }

  function isNonSystemDimension(field?: FlamingoTypes.ModeField): field is FlamingoTypes.ModeField {
    return field?.role === Attr.Role.Dimension && !Switch.Fields.isSystem(field);
  }

  function makeFilter(
    model: SchemaModel,
    encoding: Encoding,
    encField: EncField,
    value: ConditionValue,
    mappingFuncs: ValueMappingFunctions = DefaultValueMappings
  ): EncDiscreteFilterField | void {
    const field = model.makeField(encField);
    if (isNonSystemDimension(field)) {
      const selection = makeSelection(model.compileField(field), [value], mappingFuncs);
      if (selection) {
        return {
          ...encField,
          filterType: FilterType.Discrete,
          selection,
        };
      }
    }
  }

  function makeSeriesFilter(
    model: SchemaModel,
    encoding: Encoding,
    encField: EncField,
    value: ConditionValue
  ): EncDiscreteFilterField | void {
    const field = model.makeField(encField);
    if (isNonSystemDimension(field)) {
      const compiledField = model.compileField(field);
      const mappingFuncs = {
        ...DefaultValueMappings,
        utcTimestamp: makeSeriesUTCTimestampValueMapper(compiledField),
      };
      const selection = makeSelection(compiledField, [value], mappingFuncs);
      if (selection) {
        return {
          ...encField,
          filterType: FilterType.Discrete,
          selection,
        };
      }
    }
  }

  function makeSeriesUTCTimestampValueMapper(compiledField: CompiledField) {
    // The series value is formatted in HighCharts (The x and y are raw).
    // There are specific mapping functions for the 3 dateparts which
    // are not numbers. This generic function handles all of the datetruncs.
    if (compiledField.hasDateTrunc) {
      const format = DateTruncFormats[compiledField.dateFuncUnit];
      return (value: string) => {
        const parsedDate = parse(value, format, new Date(2020, 0, 1, 0, 0, 0, 0));
        const utcDate = addMinutes(parsedDate, -1 * parsedDate.getTimezoneOffset());
        return utcDate.valueOf();
      };
    }
    return (value: any) => (value === null ? null : new Date(value).valueOf());
  }

  function makeSelection(
    field: CompiledFormula,
    values: unknown[],
    mappingFuncs: ValueMappingFunctions
  ): EncSelectionCondition | void {
    type Func = (value: unknown) => ConditionValue;
    const makeFilterSelection = (valuesFunc: Func): EncSelectionCondition => ({
      selected: [...new Set(values.map(valuesFunc))],
      mode: SelectionMode.INCLUDE,
    });

    if (!field.isValid) {
      return;
    }

    if (field.hasDateTrunc) {
      return makeFilterSelection(mappingFuncs.utcTimestamp);
    }

    if (field.hasDatePart && field.isDiscrete) {
      switch (field.dateFuncUnit) {
        case DateTime.Unit.Weekday:
          return makeFilterSelection(mappingFuncs.weekday);
        case DateTime.Unit.Month:
          return makeFilterSelection(mappingFuncs.month);
        case DateTime.Unit.Quarter:
          return makeFilterSelection(mappingFuncs.quarter);
      }
    }

    switch (field.formulaDataType) {
      case DataType.Name.String:
        return makeFilterSelection(mappingFuncs.string);
      case DataType.Name.Boolean:
        return makeFilterSelection(mappingFuncs.boolean);
      case DataType.Name.Decimal:
      case DataType.Name.Float:
      case DataType.Name.Integer:
        return makeFilterSelection(mappingFuncs.number);
      case DataType.Name.DateTime:
      case DataType.Name.Timestamp:
        // Highcharts returns a string here but muze returns a number.
        // If the first non-null entry is a valid number (parsed) then
        // create a numeric selection; create timestamps otherwise
        return makeFilterSelection(
          // eslint-disable-next-line no-eq-null, eqeqeq
          values.some((value) => value != null && Number.isFinite(+value))
            ? mappingFuncs.number
            : mappingFuncs.utcTimestamp
        );
    }
  }
}
